import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { invalidError, notice } from '@/components/pbo/notice'
import { HELPER_OPTION_TRUNCATED_MESSAGE } from '@/utils/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, ShipperScope, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { buildInventoryPriceStatusesExcelUrl } from '@/services/inventory/inventoryPriceStatus/apis'
import {
  useGetBrandNameOptions,
  useGetGoodsCodeOptions,
  useGetInventoryPriceStatuses,
  useGetShipperOptions,
} from '@/services/inventory/inventoryPriceStatus/useInventoryPriceStatusQueries'
import { GetInventoryPriceStatusesReq } from '@/services/inventory/inventoryPriceStatus/types'
import {
  EXCEL_ROW_LIMIT,
  MESSAGES,
  REASON_DETAIL_MIN_LENGTH,
} from '@/pages/inventory/inventoryPriceStatus/constants'
import { InventoryPriceStatusFormValues, toInventoryPriceStatusesReq } from '@/pages/inventory/inventoryPriceStatus/filterToRequest'
import { InventoryPriceStatusSearchQuery, toSearchQuery } from '@/pages/inventory/inventoryPriceStatus/searchQuery'
import InventoryPriceStatusPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/**
 * `unknown` 값 → 화면 문자열. 조회영역·모달 폼 버스는 값을 `unknown` 으로 준다.
 *  `String(v ?? '')` 로 바로 찍으면 배열은 `"a,b"`, 객체는 `"[object Object]"` 가 **조용히** 실린다
 *  (moms-fe #803 Sonar S6551). 배열은 첫 칸을 쓰고(멀티 컨트롤 규약), 그 밖의 객체는 빈 값으로 둔다.
 */
const asText = (v: unknown): string => {
  const x = Array.isArray(v) ? v[0] : v
  return typeof x === 'string' || typeof x === 'number' ? String(x).trim() : ''
}

const EMPTY_REQ: GetInventoryPriceStatusesReq = {}

/** 조회 실패(HTTP 400) 응답에서 서버 메시지를 꺼낸다 — 2.0 StandardResponse 는 meta.message 에 싣는다. */
const extractSearchErrorMessage = (error: unknown): string =>
  (error as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data?.meta?.message ||
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  MESSAGES.searchFailed

/**
 * API 행(camelCase) → 표 행(UPPER_SNAKE). 생성 뷰의 컬럼은 화면 설계(데모)의 키를 쓴다 —
 * 변환 없이 넘기면 표가 통째로 빈다. 원본 키도 남긴다(배선은 계속 camelCase 로 읽는다).
 */
const toViewRow = (r: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...r }
  for (const [k, v] of Object.entries(r)) {
    out[k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase()] = v // totalQty → TOTAL_QTY
    // 밑줄 없는 컬럼도 있다(totalQty → TOTALQTY). 한 벌만 내보내면 그 컬럼이 통째로 빈다.
    out[k.toUpperCase()] = v
  }
  return out
}

/**
 * 상품코드 헬퍼 후보 → 모달 표 행. 2.0 헬퍼는 (itemCode·brandName·itemName) 3컬럼을 주는데
 * 모달 표 컬럼은 화면 설계 키(BRD_NM·GDS_CD·GDS_NM·BARCODE)다 — 이름을 바꿔 넘기지 않으면
 * **표가 통째로 빈다**(고른 값도 빈 문자열로 인풋에 들어간다).
 * 바코드 컬럼은 2.0 헬퍼에 대응 값이 없다(바코드는 별도 헬퍼 소관) — 빈 칸으로 둔다.
 */
const toGoodsRow = (o: { itemCode: string | null; brandName: string | null; itemName: string | null }): Record<string, unknown> => ({
  ...o,
  BRD_NM: o.brandName ?? '',
  GDS_CD: o.itemCode ?? '',
  GDS_NM: o.itemName ?? '',
})

/** 조회영역 다건 칸 → 문자열 배열. 뷰는 배열을 주고, URL 복원은 1건일 때 문자열을 준다. */
const asList = (v: unknown): string[] => {
  const raw = Array.isArray(v) ? v : v == null || v === '' ? [] : [v]
  return raw.map((x) => String(x).trim()).filter(Boolean)
}

/**
 * ST11 재고현황(단가) — 1.0 ST07 을 신규 디자인(pbo-core)으로 재구현한 화면. (LOGISPRD-10668)
 *
 * 겉(조회영역·표·엑셀 사유 모달·서치헬퍼)은 컨덕터 뷰(`./handoff/page.generated`)가 그리고,
 * 이 파일은 **옛 훅·API·검증·세션/URL 유지를 그대로 두고** 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드).
 *
 * 옛 구현에서 그대로 가져온 판단(근거는 각 지점 주석):
 * - 클라이언트 필수 검증 없음(1.0 실측) — 값 가공도 trim/빈값 제거뿐이다(filterToRequest).
 * - 엑셀 상한 100,000행(ST02/ST03/ST07 BE 통일) · 사유는 필수 + 상세 5글자 이상.
 * - 표의 화주명은 코드가 아니라 **이름**으로 보인다(옛 그리드 getShipperName 매핑).
 *
 * ── UI 가 달라지며 생긴 차이 ──
 * - 브랜드명이 다중 선택 → **입력 + 서치헬퍼**가 됐다. 조회 요청은 그대로 다건 파라미터(stcBrdNm)라
 *   고른 한 건을 배열로 싣는다. 여러 브랜드를 함께 보던 경로는 BE 계약이 아니라 화면에서 사라졌다.
 * - 서치헬퍼 후보는 **모달 안 [조회]를 눌렀을 때만** 받는다(옛 goodsHelperQuery·1.0 헬퍼 동치) — 뷰가 [조회]·엔터 때
 *   그 시점의 모달 값을 `wiring.rows.ROWS2/ROWS3` 의 filters 로 준다(__applied = 눌렀음 · 열 때·[초기화] 때 비운다).
 */
function InventoryPriceStatus({ coMingle = false }: Readonly<{ coMingle?: boolean }>) {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<InventoryPriceStatusSearchQuery>()

  // 화주 고정 계정은 자기 화주만 본다(옛 필터 `disabled={!canEditShipper}` — 판정은 2.0 공용 규칙 shipperScopeOf, LOGISPRD-14652).
  const sessionShipperRef = useRef<string>()
  const canEditShipperRef = useRef<boolean>()
  /** 세션에 있는 화주 **이름** — 목록 보강 옵션의 라벨은 여기서 온다(코드→이름 Map 은 그 경우 정의상 비어 있다). */
  const sessionShipperNameRef = useRef<string>()
  const scopeRef = useRef<ShipperScope>()
  if (sessionShipperRef.current === undefined) {
    const u = AuthStorage.user.get()
    // 현재 세션 화주(shipper_code) — 화주 전환을 반영한다(LOGISPRD-14651). 잠금 판정도 같은 공용 규칙이다(LOGISPRD-14652).
    //  재고통합 ON 이면 잠긴 29CM·INOOINT 가 MUSINSA 도 고른다(LOGISPRD-15610).
    const scope = withCoMingle(shipperScopeOf(u), coMingle)
    scopeRef.current = scope
    sessionShipperRef.current = scope.shipperCode
    canEditShipperRef.current = !scope.locked
    sessionShipperNameRef.current = String(u.shipperName ?? '')
  }
  const scope = scopeRef.current as ShipperScope
  const forcedShipper = canEditShipperRef.current ? undefined : sessionShipperRef.current || undefined
  /** 화주 고정 계정은 고를 수 있는 화주(자기 화주, 재고통합 ON 이면 MUSINSA)만 쓴다 — 그 밖의 값은 자기 화주로 덮는다. 고정이 아니면 받은 값 그대로다 */
  const pinShipper = useCallback((value: string): string => (forcedShipper ? scopedShipperCode(scope, value) : value), [forcedShipper, scope])

  /** 조회영역 값 → 옛 폼 값. 옛 변환기(toInventoryPriceStatusesReq·toSearchQuery)를 그대로 쓰기 위한 다리. */
  const toFormValues = useCallback(
    (filters: Record<string, unknown>): InventoryPriceStatusFormValues => {
      const brand = asText(filters.BRD_NM)
      return {
        // 권한 밖 화주로 조회되지 않게 고정 계정은 세션 화주로 덮는다(옵션·초기값과 같은 규칙).
        shipperCode: pinShipper(asText(filters.SPR_NM)),
        // 브랜드는 다건 파라미터(stcBrdNm)라 고른 한 건을 배열로 싣는다.
        brandName: brand ? [brand] : [],
        goodsCode: asText(filters.GDS_CD) || undefined,
        barcodes: asList(filters.BARCODE),
      }
    },
    [pinShipper],
  )

  /**
   * 남겨 둔 조회 조건(URL) — 조회영역 초기값(filterInitial)과 진입 조회가 같은 값을 쓴다. 비면 첫 진입이다.
   *  화주 고정 계정이면 URL 의 화주를 자기 화주로 덮는다(권한 밖 조회를 URL 로 뚫지 못하게).
   */
  const [restored] = useState(() => {
    const q = parsedQs as Partial<InventoryPriceStatusSearchQuery>
    const r: Record<string, unknown> = {}
    if (q.shipperCode) r.SPR_NM = String(q.shipperCode)
    if (q.brandName) r.BRD_NM = asList(q.brandName)[0] ?? ''
    if (q.goodsCode) r.GDS_CD = String(q.goodsCode)
    if (q.barcodes) r.BARCODE = asList(q.barcodes)
    if (!Object.keys(r).length) return undefined
    if (forcedShipper) r.SPR_NM = pinShipper(asText(r.SPR_NM))
    return r
  })
  // 남겨 둔 조건이 있으면 그 조건으로 시작한다 — 아래 진입 조회가 이 키로 refetch 한다(LOGISPRD-14439).
  const [queryParams, setQueryParams] = useState<GetInventoryPriceStatusesReq>(() =>
    restored ? toInventoryPriceStatusesReq(toFormValues(restored)) : EMPTY_REQ,
  )

  // 첫 진입엔 조회하지 않는다 — 조회 버튼으로만 실행한다(무필터 조회는 BE 가 400 으로 막는다). 남겨 둔 조건으로 돌아왔을 때만 아래 진입 조회가 돈다.
  const { items, isFetching, refetch } = useGetInventoryPriceStatuses(queryParams)
  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload({ fileNamePrefix: '재고현황(단가)' })

  // 화주명 셀렉트와 표의 화주명 컬럼(코드→명)이 같은 목록을 쓴다.
  const { options: shipperOptions } = useGetShipperOptions()
  const shipperNameByCode = useMemo(() => new Map(shipperOptions.map((o) => [String(o.value), String(o.label)])), [shipperOptions])
  const shipperNameOf = useCallback((code: string) => shipperNameByCode.get(code) ?? code, [shipperNameByCode])

  // 사유코드 콤보 — 옛 모달이 쓰던 공용 콤보 그대로(코드/명 배열이 짝을 이룬다. 명이 비면 코드로 대체).
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code: string, idx: number) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  /*
   * 헬퍼 후보의 화주 스코프는 **조회영역의 지금 값**을 따라간다 — 뷰가 값 변경을 알려 주지 않으므로
   *  표 슬롯이 매 렌더 주는 조회영역 값에서 화주만 뽑아 상태로 올린다(값이 실제로 바뀔 때만).
   *  브랜드는 여기서 보지 않는다 — 후보를 좁히는 조건은 **모달 안 [조회]의 적용 조건**이 출처다(아래 syncXxxQuery).
   */
  const [derivedShipper, setDerivedShipper] = useState('')
  const derivedShipperRef = useRef('')
  const syncFromFilters = useCallback((filters?: Record<string, unknown>) => {
    if (!filters) return
    const next = asText(filters.SPR_NM)
    if (next === derivedShipperRef.current) return
    derivedShipperRef.current = next
    queueMicrotask(() => setDerivedShipper(next))
  }, [])

  /*
   * 서치헬퍼 모달이 열려 있는 동안에만 후보를 부른다 — 옛 구현의 `isGoodsHelperOpen` 게이트 자리다
   *  (옛 훅 JSDoc: "모달이 닫혀 있는 동안 미리 조회하지 않도록 enabled(모달 open)로 게이트한다").
   *  뷰가 wiring.onModal 로 열림을 알려 준다.
   */
  const [helperOpen, setHelperOpen] = useState({ brd: false, gds: false })
  /*
   * 후보를 좁히는 조건은 **모달 안 [조회]를 누른 시점의 값**이다(옛 goodsHelperQuery — 입력만으로는 받지 않는다).
   *  뷰가 매 렌더 ROWS2/ROWS3 의 filters 로 그 적용 조건을 준다 — 바뀔 때만 상태로 올린다
   *  (렌더 중 setState 를 피해 마이크로태스크로).
   */
  const [brdQuery, setBrdQuery] = useState({ applied: false, brand: '' })
  const brdQueryRef = useRef(brdQuery)
  const syncBrdQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = { applied: Boolean(filters?.__applied), brand: asText(filters?.BRD_NM) }
    const cur = brdQueryRef.current
    if (next.applied === cur.applied && next.brand === cur.brand) return
    brdQueryRef.current = next
    queueMicrotask(() => setBrdQuery(next))
  }, [])
  const [gdsQuery, setGdsQuery] = useState({ applied: false, brand: '', item: '' })
  const gdsQueryRef = useRef(gdsQuery)
  /** 마지막으로 본 적용 조건 객체 — [조회]를 누른 순간을 가린다(같은 값 재조회도 안내). */
  const gdsAppliedObjRef = useRef<unknown>(undefined)
  const syncGdsQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = { applied: Boolean(filters?.__applied), brand: asText(filters?.BRD_NM), item: asText(filters?.GDS_NM) }
    // [조회]마다 뷰가 새 적용 조건 객체를 준다(상태라 다시 그려도 같은 객체) — 같은 값으로 다시 눌러도 다시 안내하려고 객체로 판정한다.
    const pressed = filters !== gdsAppliedObjRef.current && next.applied
    gdsAppliedObjRef.current = filters
    if (pressed) {
      queueMicrotask(() => {
        // 조건 없이 누르면 받지 않는다(아래 enabled) — 말없이 0건이면 고장처럼 보이므로 알린다.
        if (!next.brand && !next.item) notice.invalid(MESSAGES.goodsHelperConditionRequired)
      })
    }
    const cur = gdsQueryRef.current
    if (next.applied === cur.applied && next.brand === cur.brand && next.item === cur.item) return
    gdsQueryRef.current = next
    queueMicrotask(() => setGdsQuery(next))
  }, [])

  // 화주가 정해져야 받는다 — 1.0 헬퍼는 화주 범위 안 distinct 라 화주 없이는 의미가 없다.
  const helperShipper = pinShipper(derivedShipper)
  /*
   * 브랜드 후보 — 모달 [조회] 때의 브랜드명을 서버로 보내 부분일치로 받는다(비우면 그 화주의 전체 브랜드).
   *  예전엔 화주 전건을 받아 화면에서 걸렀지만(#792 리뷰: queryKey 를 화주 하나로 두고 캐시 재사용),
   *  2.0 헬퍼는 1,000행에서 잘라 그 뒤 브랜드는 화면에서 걸러도 안 나온다 — 그래서 서버에서 거른다(LOGISPRD-14184).
   */
  const brandHelperEnabled = Boolean(helperShipper) && helperOpen.brd && brdQuery.applied
  const { items: brandOptions, isTruncated: isBrandTruncated, dataUpdatedAt: brandUpdatedAt } = useGetBrandNameOptions(
    { shipperCode: helperShipper, brandName: brdQuery.brand || undefined },
    brandHelperEnabled,
  )
  // 2.0 헬퍼는 1,000행에서 자른다 — 모달 [조회]로 받은 결과가 상한에 닿았을 때 알린다.
  //  게이트를 함께 보는 이유: 모달을 닫으면 적용 조건이 비워져 queryKey 가 캐시된 '조건 없음' 키로 돌아가는데,
  //  v5 는 disabled 여도 캐시 data 를 돌려줘 닫힌 화면에 안내가 뜬다. 다시 열고 [조회]하면 게이트가 켜져 다시 알린다.
  useEffect(() => {
    if (brandHelperEnabled && isBrandTruncated) notice.failure(HELPER_OPTION_TRUNCATED_MESSAGE)
  }, [brandHelperEnabled, isBrandTruncated, brandUpdatedAt])
  const brandRows = useMemo(() => brandOptions.map((name: string) => ({ BRD_NM: name })) as unknown as Row[], [brandOptions])
  /*
   * 상품코드 후보는 **모달이 열려 있고 [조회]를 눌렀고 브랜드·상품명 중 하나가 채워졌을 때만** 부른다 — goods-codes 는
   *  무페이징 distinct 라 대형 화주를 조건 없이 조회하면 응답이 수십 MB 가 되어 브라우저가 멎는다
   *  (옛 구현 주석 · dev 실기동 확인). 화주 고정 계정은 마운트 때부터 화주가 있어, 모달 게이트가 없으면
   *  **화면 진입 즉시** 그 요청이 나갔다(2026-09-21 #792 리뷰). 조건은 서버가 받으므로 클라이언트에서 다시 거르지 않는다.
   */
  const { items: goodsOptions } = useGetGoodsCodeOptions(
    { shipperCode: helperShipper || undefined, brandName: gdsQuery.brand || undefined, itemName: gdsQuery.item || undefined },
    Boolean(helperShipper) && (Boolean(gdsQuery.brand) || Boolean(gdsQuery.item)) && helperOpen.gds && gdsQuery.applied,
  )

  /*
   * 진입 조회 — 남겨 둔 조건으로 돌아오면(탭을 다녀옴) 그 조건으로 한 번 조회한다. 조회영역만 되살고 표가 비던
   *  문제(LOGISPRD-14439). [조회]에 클라이언트 필수 검증이 없어 화주가 아니라 남겨 둔 조건 유무로 가른다.
   */
  const didRestoreSearch = useRef(false)
  useEffect(() => {
    if (didRestoreSearch.current) return
    didRestoreSearch.current = true
    if (!restored) return
    refetch()
      .then(({ error }) => { if (error) notice.failure(extractSearchErrorMessage(error)) })
      .catch((e: unknown) => notice.failure(extractSearchErrorMessage(e)))
  }, [restored, refetch])

  const viewRows = useMemo(
    () =>
      items.map((r) => {
        const row = toViewRow(r as unknown as Record<string, unknown>)
        // 표의 '화주명' 은 코드가 아니라 이름이다(옛 그리드 매핑). 코드는 camelCase 키에 남는다.
        return typeof row.SPR_NM === 'string' && row.SPR_NM ? { ...row, SPR_NM: shipperNameOf(row.SPR_NM) } : row
      }) as unknown as Row[],
    [items, shipperNameOf],
  )

  const wiring: Wiring = {
    permission: usePermissionSlot('st11'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { st07Table: coreTableLayoutSlot('ST11_HEAD') },
    rows: {
      /*
       * 조회는 전건을 한 번에 받는다(BE 가 페이징을 안 준다) — total 을 주지 않아 뷰가 클라이언트 페이징한다
       *  (페이지 자르기·행이 줄 때 마지막 페이지 보정·'목록 검색' 전건 적용, LOGISPRD-15349).
       *  total 을 주면 뷰가 서버 페이징으로 보고 받은 행을 한 페이지에 다 그린다.
       */
      ROWS: (_demo, q) => {
        syncFromFilters(q.filters)
        return { rows: viewRows, loading: isFetching }
      },
      // 서치헬퍼 후보 표 — q.filters 는 모달 [조회]의 적용 조건이다(모달 표는 뷰가 그린다).
      // 브랜드 후보는 문자열 목록이라 표 행으로 감싼다(상품코드는 컬럼 키로 바꿔 넘긴다).
      ROWS2: (_demo, q) => { syncBrdQuery(q?.filters); return brandRows },
      ROWS3: (_demo, q) => { syncGdsQuery(q?.filters); return goodsOptions.map(toGoodsRow) as unknown as Row[] },
    },
    actions: {
      /** [조회] — 옛 handleSearch 그대로: 세션·URL 저장 → 조건 커밋 후 재조회(클라이언트 필수 검증 없음). */
      search: async (filters) => {
        const values = toFormValues(filters)

        // 마지막 조회 조건을 메뉴 세션·URL 에 남긴다 — 탭을 옮겼다 돌아와도 그대로 복원된다.
        const searchQuery = toSearchQuery(values)
        MenuStorage.menuOnSession.setSearch(searchQuery)
        reload()
        replaceQuery(searchQuery)

        // queryParams 커밋 전에 refetch 하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다.
        flushSync(() => setQueryParams(toInventoryPriceStatusesReq(values)))
        const { error } = await refetch()
        // 서버 오류 메시지는 알럿으로 표시하고 표를 비운다(1.0 공통) — 실패 시 items 가 빈 배열이 된다.
        if (error) notice.failure(extractSearchErrorMessage(error))
      },
      /** 엑셀 — 화면에 떠 있는 결과와 **같은 조건**으로 받는다(폼 현재값이 아니라 마지막 조회 조건). */
      // 확인 슬롯은 **반환값이 없다(void)** — 생성 뷰가 토스트·닫기를 한다. 예전엔 `false` 를 돌려 토스트를
      //  막았는데, 이 화면 모달엔 토스트 문구 자체가 없어 **필요 없는 값**이었다(골격 wiring.skeleton.ts 도
      //  `async () => {}` 를 정본으로 준다). false 를 남겨 두면 생성본이 그 값을 '실패' 로 읽게 바뀌는 순간
      //  모달이 안 닫힌다 — 2026-09-21 에 실제로 그렇게 깨졌다. 실패는 throw 로만 알린다.

      st07DownloadModal_ok: async (ctx): Promise<void> => {
        const reasonCode = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
        const reasonDetail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
        // 사유 필수·상세사유 최소 길이 — 옛 ExcelDownloadReasonModal 의 okDisabled 검증을 그대로 옮겼다.
        //  뷰의 [확인]은 항상 눌리므로(모달 버튼 잠금 슬롯이 없다) 여기서 막고 throw 해 모달을 열어 둔다.
        if (!reasonCode) { throw invalidError(MESSAGES.excelReasonRequired) }
        if (reasonDetail.length < REASON_DETAIL_MIN_LENGTH) {
          throw invalidError(MESSAGES.excelReasonDetailTooShort)
        }
        // 엑셀 컬럼 구성은 조회 대상 화주가 아니라 로그인 세션 화주로 정해진다(1.0 MOMS_GRID_COLUMN_INFO 계약).
        const viewerShipperCode = AuthStorage.user.get().SPR_NM ?? ''
        onDownloadExcel(buildInventoryPriceStatusesExcelUrl({ ...queryParams, viewerShipperCode, reasonCode, reasonDetail }))
      },
      // 서치헬퍼 [선택] — 고른 값을 인풋에 넣는 일은 뷰가 한다.
      st07BrdSearchModal_ok: () => {},
      st07GdsSearchModal_ok: () => {},
    },
    options: {
      /**
       * 화주 목록 — 통합 운영 계정만 전체를 본다(그 외는 고를 수 있는 화주만 — 옛 필터의 disabled 대체).
       * 로그인 화주가 목록에 없어도(내부 운영 계정) 기본값이 보이도록 보강한다.
       * 재고통합 ON 의 29CM·INOOINT 는 자기 화주와 MUSINSA 둘이다(LOGISPRD-15610).
       */
      ST07SHIPPER_OPTIONS: () => {
        const mine = sessionShipperRef.current ?? ''
        /*
         * ⚠️ 이 분기는 `mine` 이 화주 목록에 **없을 때만** 탄다 — 그러면 코드→이름 Map(shipperNameByCode)에도
         *  정의상 없어서 shipperNameOf 는 늘 코드를 그대로 돌려준다. 라벨이 이름이 아니라 코드로 보였다
         *  (화주 고정 계정은 이 옵션이 유일 선택지라 **항상** 코드가 보였다, moms-fe #792 리뷰).
         *  세션의 화주 이름을 쓴다 — 옛 구현(InventoryPriceStatusFilter `user.shipperName || defaultCode`)과 같다.
         */
        if (canEditShipperRef.current) {
          return (mine && !shipperOptions.some((o) => String(o.value) === mine)
            ? [{ value: mine, label: sessionShipperNameRef.current || mine }, ...shipperOptions]
            : shipperOptions) as Option[]
        }
        return selectableShippers(scope).map((s) =>
          shipperOptions.find((o) => String(o.value) === s.shipperCode)
            ?? { value: s.shipperCode, label: (s.shipperCode === mine ? sessionShipperNameRef.current : s.shipperName) || s.shipperCode },
        ) as Option[]
      },
      /**
       * 사유코드 — 1.0 공용 콤보(DOWNLOAD_CD). 데모 옵션은 화면 설계용 예시라 실제 감사로그 코드와 다르다.
       * 콤보가 비면(로딩·실패) 빈 목록을 준다 — 없는 코드를 고르게 두는 쪽이 더 나쁘다.
       */
      REASONRADIO_OPTIONS: () => reasonOptions as Option[],
    },
    /** 조회영역 기본값 — 화주는 기본 화주([defaultShipper], 재고통합 대상 계정은 MUSINSA, LOGISPRD-15719). 브랜드·상품코드·바코드는 비운다. */
    /**
     * 화주 고정 계정은 화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 pinShipper 가 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르므로 잠그지 않는다(LOGISPRD-15610)
     */
    filterDisabled: canSelectShipper(scope) ? undefined : { SPR_NM: true },
    filterDefaults: {
      st07FilterCard: (demo) => ({
        ...demo,
        /*
         * 세션 화주가 비어 있으면 **undefined 로 덮는다** — 안 덮으면 데모 기본값 `"무신사스탠다드"` 가 남는다.
         *  그 값은 화주 **이름**인데 조회 요청의 `sprNm` 은 화주 **코드**를 EQUAL 로 비교하므로 0건이 나오고,
         *  서치헬퍼도 그 이름을 화주로 보고 호출된다(moms-fe #792 리뷰). 옛 buildDefaultValues 도 undefined 였다.
         *  ⚠️ 생성본 셀렉트는 값이 undefined 여도 데모 이름을 대신 보인다(page.generated.tsx `value ?? "무신사스탠다드"`)
         *   — 화면 표시는 codegen 소관이라 여기서 못 고친다. 요청에 실리는 값은 이 줄이 결정한다.
         */
        SPR_NM: defaultShipper(scope).shipperCode || undefined,
        BRD_NM: '',
        GDS_CD: '',
        BARCODE: [],
      }),
    },
    /** 진입 초기값 — 남겨 둔 조회 조건(위 restored)이 기본값을 덮는다. 화주 고정 계정이면 URL 이 화주를 덮지 못한다. */
    filterInitial: {
      st07FilterCard: (defaults) => ({ ...defaults, ...restored }),
    },
    useBusy: () => isDownloading,
    opens: {
      /** 엑셀 — 결과가 있어야 하고 상한을 넘으면 안 된다(옛 handleDownloadClick). */
      st07ExcelButton: () => {
        if (items.length === 0) { notice.invalid(MESSAGES.excelNoRows); return false }
        // BE MAX_EXCEL_ROW_SIZE(100,000) count 선검사와 같은 기준으로 선차단한다.
        if (items.length > EXCEL_ROW_LIMIT) { notice.invalid(MESSAGES.excelRowLimitExceeded); return false }
        return true
      },
    },
    /** 서치헬퍼 모달 열림 — 후보 조회를 이 신호로 게이트한다(옛 isBrandHelperOpen·isGoodsHelperOpen). */
    onModal: (open) => {
      setHelperOpen((s) =>
        s.brd === open.st07BrdSearchModal && s.gds === open.st07GdsSearchModal
          ? s
          : { brd: open.st07BrdSearchModal, gds: open.st07GdsSearchModal },
      )
      // 닫히면 그 모달의 적용 조건을 비운다 — 남겨 두면 다시 열 때 뷰가 빈 조건을 주기 전 한 렌더 동안
      //  '열림 + 지난 [조회] 조건'이 되어 후보 요청이 한 번 나간다(#861 리뷰).
      if (!open.st07BrdSearchModal && brdQueryRef.current.applied) { brdQueryRef.current = { applied: false, brand: '' }; setBrdQuery({ applied: false, brand: '' }) }
      if (!open.st07GdsSearchModal && gdsQueryRef.current.applied) { gdsQueryRef.current = { applied: false, brand: '', item: '' }; setGdsQuery({ applied: false, brand: '', item: '' }) }
    },
    /** [초기화] — 남겨 둔 조회 조건도 함께 지운다(조회는 트리거하지 않는다). */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      // 탭바는 reload 로만 새 조건을 읽는다 — 빼면 탭 링크에 지운 조건이 남아 복귀 시 진입 조회가 그 조건으로 돈다.
      reload()
      replaceQuery({})
    },
  }

  return <InventoryPriceStatusPageView wiring={wiring} />
}

/** 화면은 화주 범위를 첫 렌더에 고정하므로 재고통합 FF 값이 정해진 뒤에 그린다(LOGISPRD-15610) */
function InventoryPriceStatusEntry() {
  const { ready, coMingle } = useCoMingleShipperScope()
  return ready ? <InventoryPriceStatus coMingle={coMingle} /> : <div role="status">재고금액현황을 준비하고 있어요.</div>
}

export default InventoryPriceStatusEntry
